import { AdminLayout } from '@/modules/common/layouts/AdminLayout';
import { PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER } from '@/modules/payments/constants/payment-group-stage';
import { ListPaymentsPage } from '@/modules/payments/pages/ListPaymentsPage';
import {
  PaymentPageStage,
  PaymentPageTab,
  PaymentsSearchParams,
} from '@/modules/payments/types';
import { PaymentStatus } from '@/types/payment';
import { PaymentGroupStage } from '@/types/payment-groups';
import { createFileRoute } from '@tanstack/react-router';
import * as yup from 'yup';

export const Route = createFileRoute('/_admin/payments/')({
  validateSearch: (search: Record<string, unknown>): PaymentsSearchParams => {
    const stage = yup
      .string()
      .oneOf(Object.values(PaymentPageStage) as string[])
      .optional()
      .validateSync(search.stage) as PaymentPageStage | undefined;

    const getAllowedStatuses = (
      selectedStage?: PaymentPageStage,
    ): PaymentStatus[] => {
      if (!selectedStage || selectedStage === PaymentPageStage.ALL) {
        return Object.values(PaymentStatus);
      }

      const stageMapping: Record<PaymentPageStage, PaymentGroupStage> = {
        [PaymentPageStage.ALL]: PaymentGroupStage.AWAITING_PAYMENT, // This won't be used due to the check above
        [PaymentPageStage.AWAITING_PAYMENT]: PaymentGroupStage.AWAITING_PAYMENT,
        [PaymentPageStage.IN_PROGRESS]: PaymentGroupStage.IN_PROGRESS,
        [PaymentPageStage.FAILED]: PaymentGroupStage.FAILED,
        [PaymentPageStage.COMPLETED]: PaymentGroupStage.COMPLETED,
      };

      const groupStage = stageMapping[selectedStage];
      return PAYMENT_GROUP_STAGE_TO_STATUS_MAPPER[groupStage] || [];
    };

    const allowedStatuses = getAllowedStatuses(stage);

    return {
      page: yup.number().integer().min(1).optional().validateSync(search.page),
      limit: yup
        .number()
        .integer()
        .min(1)
        .optional()
        .validateSync(search.limit),
      status: yup
        .lazy(val => {
          if (Array.isArray(val)) {
            return yup
              .array(yup.string().oneOf(allowedStatuses).required())
              .optional();
          }
          return yup.string().oneOf(allowedStatuses).optional();
        })
        .validateSync(search.status),
      tab: yup
        .string()
        .oneOf(Object.values(PaymentPageTab))
        .optional()
        .validateSync(search.tab),
      educationProvider: yup
        .lazy(val =>
          Array.isArray(val)
            ? yup.array(yup.string().required()).optional()
            : yup.string().optional(),
        )
        .validateSync(search.educationProvider),
      stage,
    };
  },
  component: () => {
    return (
      <AdminLayout>
        <ListPaymentsPage />
      </AdminLayout>
    );
  },
});
